import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X, LifeBuoy } from "lucide-react";

const navItems = [
  { id: "about", label: "Σχετικά" },
  { id: "services", label: "Υπηρεσίες" },
  { id: "approach", label: "Προσέγγιση" },
  { id: "sessions", label: "Συνεδρίες" },
  { id: "insights", label: "Άρθρα" },
  { id: "faq", label: "FAQ" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    if (id === "hero") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/40"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20 py-5 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          data-testid="logo-link"
          className="flex items-baseline gap-2 group"
        >
          <span className="font-heading text-2xl sm:text-[26px] tracking-tight font-medium text-foreground">
            Άννα <span className="italic font-light">Παπαχρονόπουλου</span>
          </span>
          <span className="hidden sm:inline text-[11px] tracking-[0.25em] uppercase text-muted-foreground font-body">
            Ψυχολόγος
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              data-testid={`nav-${item.id}`}
              className="text-sm font-body text-foreground/70 hover:text-foreground transition-colors duration-300 relative group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <button
          onClick={() => scrollTo("contact")}
          data-testid="header-cta-button"
          className="hidden md:inline-flex items-center rounded-full bg-foreground text-background px-6 py-3 text-sm font-body font-medium hover:bg-foreground/85 transition-all duration-300 hover:-translate-y-0.5"
        >
          Κλείσε Συνεδρία
        </button>

        <Link
          to="/krisi-panikou"
          data-testid="header-sos-link"
          className="hidden md:inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/[0.06] text-foreground/80 hover:text-foreground hover:bg-primary/10 px-4 py-2.5 text-xs font-body tracking-wide transition-all"
          aria-label="Σύντομος οδηγός για κρίση πανικού"
        >
          <LifeBuoy size={13} strokeWidth={1.5} className="text-primary" />
          Κρίση πανικού;
        </Link>

        <button
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 -mr-2 text-foreground"
          aria-label="Μενού"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="mobile-menu"
            className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border/40 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="text-left py-3 font-heading text-2xl text-foreground/80 hover:text-foreground transition-colors border-b border-border/30"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("contact")}
                data-testid="mobile-header-cta-button"
                className="mt-5 rounded-full bg-foreground text-background px-6 py-4 text-sm font-body font-medium"
              >
                Κλείσε Συνεδρία
              </button>
              <Link
                to="/krisi-panikou"
                onClick={() => setOpen(false)}
                data-testid="mobile-header-sos-link"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full border border-primary/40 bg-primary/[0.06] text-foreground/80 px-6 py-3 text-sm font-body"
              >
                <LifeBuoy size={14} strokeWidth={1.5} className="text-primary" />
                Κρίση πανικού; — Σύντομος οδηγός
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
